import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@config/axios/axios.config";
import { QUERY_KEYS, SERVER_ROUTES } from "@constants/api.routes";
import type { SelectOptionsResponse } from "../types/select-option.types";
import { showNotification } from "@libs/notistack/notificationStack";

export interface CreateSkillPayload {
	name: string;
	description: string;
	isActive: boolean;
}

export interface CreateSkillResponse {
	success: boolean;
	message: string;
	data?: {
		uuid: string;
		name: string;
		id?: number;
	};
}

// Interface for paginated response
interface PaginatedSelectOptionsResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: {
		data: Array<{
			id: number;
			uuid: string;
			name: string;
			label?: string;
			value?: string;
		}>;
		pagination: {
			currentPage: number;
			perPage: number;
			totalItems: number;
			totalPages: number;
			hasNextPage: boolean;
			hasPreviousPage: boolean;
		};
	};
}

export function useGetJobSkills(enabled: boolean = true, useInfinite: boolean = false) {
	// Regular query for backward compatibility
	const regularQuery = useQuery<SelectOptionsResponse | PaginatedSelectOptionsResponse>({
		queryKey: [QUERY_KEYS.INSTITUTES.JOB_SKILLS, "regular"],
		queryFn: async () => {
			const response = await api.get<SelectOptionsResponse | PaginatedSelectOptionsResponse>(
				`${SERVER_ROUTES.INSTITUTES.JOB_SKILLS}?page=1&limit=100`
			);
			return response.data;
		},
		enabled: enabled && !useInfinite,
	});

	// Infinite query for pagination support
	const infiniteQuery = useInfiniteQuery<PaginatedSelectOptionsResponse | SelectOptionsResponse>({
		queryKey: [QUERY_KEYS.INSTITUTES.JOB_SKILLS, "infinite"],
		queryFn: async ({ pageParam = 1 }) => {
			const response = await api.get<PaginatedSelectOptionsResponse | SelectOptionsResponse>(
				`${SERVER_ROUTES.INSTITUTES.JOB_SKILLS}?page=${pageParam}&limit=20`
			);
			return response.data;
		},
		enabled: enabled && useInfinite,
		getNextPageParam: (lastPage) => {
			// Check if response has pagination structure
			if (!lastPage) return undefined;
			
			// Check if it's a paginated response
			if ("data" in lastPage && lastPage.data && typeof lastPage.data === "object" && "pagination" in lastPage.data) {
				const pagination = (lastPage.data as { pagination?: { hasNextPage?: boolean; currentPage?: number } }).pagination;
				if (pagination && pagination.hasNextPage && pagination.currentPage !== undefined) {
					return pagination.currentPage + 1;
				}
			}
			
			// If no pagination info or it's a simple array response, return undefined (no more pages)
			return undefined;
		},
		initialPageParam: 1,
	});

	// Process regular query data
	const regularData = regularQuery.data;
	const regularSkillsArray = 
		(regularData && "data" in regularData && Array.isArray(regularData.data)) 
			? regularData.data 
			: (regularData && "data" in regularData && "data" in regularData.data && Array.isArray(regularData.data.data))
				? regularData.data.data
				: [];

	// Process infinite query data
	const infiniteSkillsArray = (infiniteQuery.data?.pages.flatMap(page => {
		// Handle both paginated and non-paginated responses
		if (page && "data" in page) {
			// Check if it's a paginated response
			if (page.data && typeof page.data === "object" && "data" in page.data && Array.isArray(page.data.data)) {
				return page.data.data as unknown[];
			}
			// Check if it's a simple array response
			if (Array.isArray(page.data)) {
				return page.data as unknown[];
			}
		}
		return [] as unknown[];
	}) ?? []) as unknown[];

	// Combine and map to SelectOption format
	type SkillItem = { id?: number | string; uuid?: string; name?: string; label?: string; value?: string };
	const skillsArray = (useInfinite ? infiniteSkillsArray : regularSkillsArray) as SkillItem[];
	const allSkills = skillsArray.map(skill => ({
		id: String(skill.id || ""),
		uuid: skill.uuid || "",
		value: String(skill.uuid || skill.id || ""),
		label: skill.name || skill.label || "",
		name: skill.name || "",
	}));

	return {
		jobSkills: allSkills,
		loadingJobSkills: useInfinite ? infiniteQuery.isLoading : regularQuery.isLoading,
		error: useInfinite ? infiniteQuery.error : regularQuery.error,
		// Infinite query specific
		hasNextPage: infiniteQuery.hasNextPage,
		fetchNextPage: infiniteQuery.fetchNextPage,
		isFetchingNextPage: infiniteQuery.isFetchingNextPage,
	};
}

// Hook to create a new job skill
export function useCreateJobSkill() {
	const queryClient = useQueryClient();

	return useMutation<CreateSkillResponse, Error, CreateSkillPayload>({
		mutationFn: async (payload: CreateSkillPayload) => {
			const response = await api.post<CreateSkillResponse>(SERVER_ROUTES.INSTITUTES.JOB_SKILLS, payload);
			return response.data;
		},
		onSuccess: (data) => {
			// Invalidate job skills query to refresh the list
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INSTITUTES.JOB_SKILLS] });
			showNotification({
				message: data.message || "Skill added successfully",
				severity: "success",
			});
		},
		onError: (error) => {
			showNotification({
				message: error.message || "Failed to add skill",
				severity: "error",
			});
		},
	});
}
